import { FilterOption } from '~/components/update-filter-options/updateFilterOptions';
import {
  INDEX_ID,
  SCREENER_ID,
  WATCHLIST_ID,
  WORKING_LIST_ID,
} from '~/config/constants';

export interface AssetsFilterOption extends FilterOption {
  customOptionLabel?: string;
  customOptionCallback?: () => void;
}

interface MapToFilterOptionsArgs<T> {
  items: T[];
  valueKey: string;
  customHeaderTitle?: string;
  hasBottomBorder?: boolean;
  withDefaultOption?: boolean;
  customOptionLabel?: string;
  customOptionCallback?: () => void;
}

export type AssetFilterValueKey =
  | typeof WORKING_LIST_ID
  | typeof WATCHLIST_ID
  | typeof SCREENER_ID
  | typeof INDEX_ID;

export const mapToFilterOptions = <T extends { id: string; name: string }>({
  items,
  valueKey,
  customHeaderTitle,
  hasBottomBorder = false,
  withDefaultOption = false,
  ...rest
}: MapToFilterOptionsArgs<T>): AssetsFilterOption[] => {
  if (withDefaultOption && items.length === 0) {
    return [
      {
        name: '',
        key: '',
        value: {},
        customHeaderTitle,
        hasBottomBorder,
        ...rest,
      },
    ];
  }

  return items.map((item, idx) => ({
    name: item.name,
    key: item.id,
    value: { [valueKey]: item.id },
    hasBottomBorder: hasBottomBorder && idx === items.length - 1,
    ...(idx === 0 && { customHeaderTitle }),
  }));
};

export const mapActiveFilterOptions = <T extends { id: string; name: string }>(
  items: T[],
  valueKey: string,
  filterOptions: FilterOption[]
): FilterOption[] => {
  const itemMap = new Map<unknown, T>(items.map(item => [item.id, item]));

  return filterOptions.reduce<FilterOption[]>((options, option) => {
    const matchedItem = itemMap.get(option.value[valueKey]);

    if (matchedItem) {
      options.push({ ...option, name: matchedItem.name });
    }

    return options;
  }, []);
};

export const isAssetFilterValueKey = (
  key: string
): key is AssetFilterValueKey =>
  key === WORKING_LIST_ID ||
  key === WATCHLIST_ID ||
  key === SCREENER_ID ||
  key === INDEX_ID;
